/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import ChecklistTaskColumnField from './ChecklistTaskColumnField'

export interface ChecklistColumnChoice {
  mode: 'select' | 'new'
  columnId: string
  newName: string
  newColor: string
}

interface TaskDraft {
  itemId: string
  text: string
  checklistTitle: string
  assignees: { name: string }[]
  due_date: string | null
  due_time: string | null
}

interface Props {
  theme: string
  task: TaskDraft
  saving: boolean
  error: string | null
  // Whether the host wired a create-task handler (gates the confirm button).
  hasCreateHandler: boolean
  // Whether to show the column picker (host wired columns + column creation).
  showColumnField: boolean
  columns: any[]
  columnChoice: ChecklistColumnChoice
  setColumnChoice: React.Dispatch<React.SetStateAction<ChecklistColumnChoice>>
  onTextChange: (text: string) => void
  onClose: () => void
  onConfirm: () => void
}

// Presentational "Create task from a checklist item" modal. All side effects
// (DOM stamping, API calls) live in the host's onConfirm — this component only
// renders the form and reports edits up, keeping TextEditor lean.
const ChecklistCreateTaskModal: React.FC<Props> = ({
  theme,
  task,
  saving,
  error,
  hasCreateHandler,
  showColumnField,
  columns,
  columnChoice,
  setColumnChoice,
  onTextChange,
  onClose,
  onConfirm,
}) => {
  const isDark = theme === 'dark'
  const hasInherited =
    task.assignees.length > 0 || (!!task.due_date && !!task.due_time)
  // Task name must be 3–300 characters (trimmed). The textarea caps input at
  // 300, so in practice only the min can fail — but we check both to be safe.
  const TITLE_MIN = 3
  const TITLE_MAX = 300
  const trimmedTitle = task.text.trim()
  const titleError =
    trimmedTitle.length === 0
      ? null // don't nag before the user types
      : trimmedTitle.length < TITLE_MIN
      ? `Task name must be at least ${TITLE_MIN} characters.`
      : trimmedTitle.length > TITLE_MAX
      ? `Task name must be ${TITLE_MAX} characters or less.`
      : null
  const titleValid =
    trimmedTitle.length >= TITLE_MIN && trimmedTitle.length <= TITLE_MAX
  const confirmDisabled =
    saving ||
    !titleValid ||
    !hasCreateHandler ||
    (columnChoice.mode === 'new' && !columnChoice.newName.trim())
  const confirmLabel = saving
    ? 'Saving…'
    : columnChoice.mode === 'new'
    ? 'Create column & save'
    : 'Create task'

  return (
    <div
      className="ql-checklist-confirm-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onClose()
      }}
    >
      <div className={`ql-checklist-confirm-modal ${isDark ? 'dark' : ''}`}>
        <div className="ql-checklist-confirm-title">Create task</div>
        <div className="ql-checklist-confirm-subtitle">
          <i className="bi bi-card-checklist" />
          From checklist:&nbsp;<b>{task.checklistTitle}</b>
        </div>
        <div className={`ql-checklist-field-label ${isDark ? 'dark' : ''}`}>
          Task name
        </div>
        <textarea
          className={`ql-checklist-task-textarea ${isDark ? 'dark' : ''}`}
          value={task.text}
          onChange={(e) => onTextChange(e.target.value)}
          autoFocus
          rows={3}
          maxLength={300}
          disabled={saving}
        />
        {titleError && <div className="ql-checklist-task-error">{titleError}</div>}
        {hasInherited && (
          <div className="ql-checklist-task-inherit">
            {task.assignees.length > 0 && (
              <span>Assignees: {task.assignees.map((a) => a.name).join(', ')}</span>
            )}
            {task.due_date && task.due_time && (
              <span>
                Due:{' '}
                {new Date(`${task.due_date}T${task.due_time}Z`).toLocaleString()}
              </span>
            )}
          </div>
        )}
        {showColumnField && (
          <ChecklistTaskColumnField
            theme={theme}
            columns={columns}
            mode={columnChoice.mode}
            onModeChange={(mode) => setColumnChoice((p) => ({ ...p, mode }))}
            selectedColumnId={columnChoice.columnId}
            onSelectColumn={(columnId) => setColumnChoice((p) => ({ ...p, columnId }))}
            newColumnName={columnChoice.newName}
            onNewColumnName={(newName) => setColumnChoice((p) => ({ ...p, newName }))}
            newColumnColor={columnChoice.newColor}
            onNewColumnColor={(newColor) => setColumnChoice((p) => ({ ...p, newColor }))}
            disabled={saving}
          />
        )}
        {error && <div className="ql-checklist-task-error">{error}</div>}
        <div className="ql-checklist-confirm-actions">
          <button
            type="button"
            className="ql-checklist-confirm-cancel"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ql-checklist-confirm-delete bg-primary"
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ChecklistCreateTaskModal
